/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Mail, Lock, User, ArrowRight, X, AlertCircle, CheckCircle, ExternalLink, KeyRound, ArrowLeft, Send, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../ReduxToolkit/Hooks';
import { loginSuccess } from '../../ReduxToolkit/Auth/AuthSlice';
import { authService } from '../../Services/AuthService';
import { toast } from 'react-toastify';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'LOGIN' | 'FORGOT_PASSWORD'>('LOGIN');
  const [isLoading, setIsLoading] = useState(false);

  // Login State
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot Password State
  const [forgotIdentifier, setForgotIdentifier] = useState('');

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);

  // Lock body scroll + close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const prev = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.document.body.style.overflow = prev;
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Reset form when re-opened
  useEffect(() => {
    if (isOpen) {
      setMode('LOGIN');
      setErrorMsg(null);
      setInfoMsg(null);
      setLoginIdentifier('');
      setLoginPassword('');
      setForgotIdentifier('');
    }
  }, [isOpen]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setInfoMsg(null);

    if (!loginIdentifier.trim() || !loginPassword.trim()) {
      setErrorMsg("Veuillez remplir tous les champs.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await authService.login({
        login: loginIdentifier.trim(),
        email: loginIdentifier.trim(),
        password: loginPassword
      });
      if (response.success && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        if (response.refreshToken) localStorage.setItem('refreshToken', response.refreshToken);
        dispatch(loginSuccess({ accessToken: response.accessToken }));
        toast.success("Connexion réussie");
        onClose();
      } else {
        setErrorMsg(response.message || "Identifiants incorrects.");
      }
    } catch (error: any) {
      setErrorMsg(error.message || "Erreur de connexion au serveur.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setInfoMsg(null);

    if (!forgotIdentifier.trim()) {
      setErrorMsg("Veuillez renseigner votre adresse e-mail ou votre nom d'utilisateur.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.forgotPassword(forgotIdentifier.trim());
      if (response.success) {
        setInfoMsg(response.message || "Un nouveau mot de passe a été envoyé à votre adresse email.");
        toast.success("Nouveau mot de passe envoyé !");
      } else {
        setErrorMsg(response.message || "Impossible de réinitialiser le mot de passe.");
      }
    } catch (error: any) {
      const serverMsg = error.message || error.data?.message || error.response?.data?.message || "Erreur lors de la réinitialisation.";
      setErrorMsg(serverMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = "w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400";
  const labelCls = "block text-[11px] font-semibold text-zinc-600 uppercase tracking-wider mb-1";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-black/40 backdrop-blur-[2px]">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl shadow-zinc-900/15 overflow-hidden border border-zinc-100 max-h-[90vh] flex flex-col animate-fade-in">

        {/* Top strip — violet with white logo */}
        <div className="bg-violet-800 px-6 py-4 flex items-center justify-between shrink-0">
          <img src="/logo_docarn_blanc.png" alt="Docarn Juridique" className="h-7 w-auto select-none pointer-events-none" draggable={false} />
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/15 transition-colors text-white/80 hover:text-white cursor-pointer border-none bg-transparent"
            title="Fermer (Échap)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Title row */}
        <div className="px-6 pt-4 pb-3 border-b border-zinc-100 shrink-0 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
              {mode === 'LOGIN' ? (
                <>Connexion à votre espace</>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 text-violet-700" />
                  Mot de passe oublié
                </>
              )}
            </h2>
            <p className="text-xs text-zinc-400 mt-0.5">
              {mode === 'LOGIN' 
                ? "Accédez à votre fonds documentaire sécurisé" 
                : "Recevez instantanément vos nouveaux identifiants par e-mail"}
            </p>
          </div>
          <button
            onClick={() => { onClose(); navigate('/register'); }}
            className="text-[11px] text-violet-700 hover:text-violet-900 flex items-center gap-1 font-medium bg-violet-50 hover:bg-violet-100 px-2.5 py-1 rounded-md transition-colors cursor-pointer"
            title="Accéder au formulaire complet d'inscription"
          >
            <span>S'inscrire</span>
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-4 flex-1">

          {/* Feedback messages */}
          {errorMsg && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 px-3.5 py-2.5 rounded-lg text-xs leading-relaxed animate-fade-in">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}
          {infoMsg && (
            <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-100 text-emerald-700 px-3.5 py-2.5 rounded-lg text-xs leading-relaxed animate-fade-in">
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{infoMsg}</span>
            </div>
          )}

          {mode === 'LOGIN' ? (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>Email ou Nom d'utilisateur</label>
                <div className="relative">
                  <User className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    required
                    placeholder="Email ou Nom d'utilisateur"
                    value={loginIdentifier}
                    onChange={(e) => setLoginIdentifier(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wider">Mot de passe</label>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('FORGOT_PASSWORD');
                      setErrorMsg(null);
                      setInfoMsg(null);
                      if (loginIdentifier) setForgotIdentifier(loginIdentifier);
                    }}
                    className="text-[11px] text-violet-700 hover:text-violet-900 font-medium cursor-pointer hover:underline bg-transparent border-none p-0"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-zinc-800 placeholder-zinc-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 focus:outline-none cursor-pointer p-0.5 bg-transparent border-none flex items-center justify-center"
                    title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-semibold text-xs rounded-lg transition-colors cursor-pointer border-none disabled:opacity-60 disabled:cursor-not-allowed mt-2 shadow-sm shadow-violet-700/20"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connexion en cours...
                  </span>
                ) : "Se connecter"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <div className="p-3 bg-violet-50/70 border border-violet-100 rounded-lg text-xs text-violet-900 leading-relaxed">
                <div className="font-semibold mb-1 flex items-center gap-1.5 text-violet-800">
                  <KeyRound className="w-3.5 h-3.5" /> Récupération instantanée
                </div>
                Entrez votre adresse e-mail ou votre nom d'utilisateur. Un nouveau mot de passe vous sera immédiatement transmis par e-mail.
              </div>

              <div>
                <label className={labelCls}>Email ou Nom d'utilisateur</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    required
                    placeholder="Ex: paul.kamga@docarn.cm ou paul_kamga"
                    value={forgotIdentifier}
                    onChange={(e) => setForgotIdentifier(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-semibold text-xs rounded-lg transition-colors cursor-pointer border-none disabled:opacity-60 disabled:cursor-not-allowed mt-2 flex items-center justify-center gap-2 shadow-sm shadow-violet-700/20"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Réinitialisation en cours...
                  </span>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Réinitialiser mon mot de passe
                  </>
                )}
              </button>
            </form>
          )}

          {/* Bottom actions & links */}
          <div className="pt-3 border-t border-zinc-100 flex items-center justify-between text-xs">
            {mode === 'FORGOT_PASSWORD' ? (
              <button
                onClick={() => {
                  setMode('LOGIN');
                  setErrorMsg(null);
                  setInfoMsg(null);
                }}
                className="text-xs text-violet-700 hover:text-violet-900 font-semibold transition-colors inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-none p-0"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour à la connexion
              </button>
            ) : (
              <button
                onClick={() => {
                  setMode('FORGOT_PASSWORD');
                  setErrorMsg(null);
                  setInfoMsg(null);
                  if (loginIdentifier) setForgotIdentifier(loginIdentifier);
                }}
                className="text-xs text-zinc-500 hover:text-violet-700 transition-colors inline-flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
              >
                Mot de passe oublié ?
                <ArrowRight className="w-3 h-3" />
              </button>
            )}

            <button
              onClick={() => {
                onClose();
                navigate('/register');
              }}
              className="text-xs text-zinc-500 hover:text-violet-700 transition-colors inline-flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
            >
              Créer un compte
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};
